import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  Cell,
  createColumnHelper,
  getCoreRowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { getTime } from 'date-fns';
import { first, isEmpty } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  RosterFilterInput,
  RosterRoomAssignmentStatus,
  useRosterListQuery,
  useRosterReportLazyQuery,
  UserRole,
  ClientPod,
  RosterListQuery,
} from '../../generated/graphql';
import { useClientRequiredFieldsAndPod } from '../../hooks/useClientRequiredFieldsAndPod';
import { useTravelerSelfCheck } from '../../hooks/useTravelerSelfCheck';
import { useAppSelector } from '../../store/store';
import { popToast } from '../../utilities/popToast';
import triggerFileDownload from '../../utilities/triggerFileDownload';
import useQueryUrl from '../../utilities/useQueryUrl';
import useQueryUrlFilters from '../../utilities/useQueryUrlFilters';
import useQueryUrlSorting from '../../utilities/useQueryUrlSorting';
import {
  Button,
  PrimaryButtonLink,
  SecondaryButton,
  TealButton,
} from '../Button';
import NewRosterRequestsMenu from '../RequestsTable/NewRosterRequestsMenu';
import Table from '../Table';
import TablePaginationFooter from '../TablePaginationFooter';
import TablePaginationHeader from '../TablePaginationHeader';

import FilterChipsContainer from './FilterChipsContainer';
import RosterFilterForm from './RosterFilterFom';
import RosterFilterSidebar from './RosterFilterSidebar';
import {
  ColumnGenerationProps,
  EnumRecordType,
  getEnergyViewColumns,
  getGovernmentViewColumns,
} from './columnDefinitions';

export type TableRosterRoomAssignment =
  RosterListQuery['roster']['items'][number];

const columnHelper = createColumnHelper<TableRosterRoomAssignment>();

const paginatedDefaults = {
  pageIndex: 0,
  pageSize: 25,
};

const RosterTable: FC = () => {
  const { noShowStatusTypes, rosterRoomAssignmentStatuses, genders } =
    useAppSelector((state) => state.enums);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectedRowsData, setSelectedRowsData] = useState<
    TableRosterRoomAssignment[]
  >([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const availableProfiles = useAppSelector((state) => state.user.roles);

  const isAdmin = availableProfiles?.some((profile) =>
    [UserRole.ClientAdmin].includes(profile),
  );

  // Get client required fields and pod from the backend
  const { clientRequiredFields, clientPod } = useClientRequiredFieldsAndPod();

  // Check if client is inactive
  const client = useAppSelector((state) => state.user.profile?.client);
  const isClientInactive = client?.isInactive ?? false;

  const {
    isOnlyTraveler,
    isOwnRosterEntry,
    hasLinkedProfile,
    clientTeamMemberId,
  } = useTravelerSelfCheck();

  const [rosterReport, { loading: loadingReport }] = useRosterReportLazyQuery({
    onError: (error) => {
      console.error(error);
      popToast('Error exporting', { type: 'error' });
    },
    onCompleted: (res) => {
      const decoded = window.atob(res.rosterReport);
      const blob = new Blob([decoded], {
        type: 'text/csv;charset=utf-8',
      });

      const timestamp = getTime(new Date());
      triggerFileDownload(blob, `roster_table_export_${timestamp}.csv`);
    },
  });

  const filterDefault = useQueryUrlFilters<RosterFilterInput>(
    {
      status: [
        RosterRoomAssignmentStatus.InHouse,
        RosterRoomAssignmentStatus.Upcoming,
      ],
    },
    ['roomBeds'],
    ['duplicateApproved'],
  );

  const sortingDefaultValues = [
    {
      id: 'status',
      desc: false,
    },
  ];

  const rawSortingDefault = useQueryUrlSorting(sortingDefaultValues);

  // Filter out invalid sort fields like 'roommates'
  const sortingDefault = useMemo(() => {
    if (!rawSortingDefault) return undefined;
    return rawSortingDefault.filter((sort) => sort.id !== 'roommates');
  }, [rawSortingDefault]);

  const [filters, setFilters] = useState<RosterFilterInput | undefined>(
    filterDefault,
  );

  const [sorting, setSorting] = useState<SortingState | undefined>(
    sortingDefault,
  );

  const [pagination, setPagination] = useState(paginatedDefaults);

  useQueryUrl(first(sorting), filters);

  const {
    data,
    loading: loadingRoster,
    fetchMore,
    refetch,
  } = useRosterListQuery({
    variables: {
      offset: paginatedDefaults.pageIndex,
      limit: paginatedDefaults.pageSize,
      sort: first(sortingDefault),
      filter: filterDefault,
    },
    notifyOnNetworkStatusChange: true,
    onError(error) {
      popToast(`Error loading roster: ${error.message}`, { type: 'error' });
    },
  });

  useEffect(() => {
    fetchMore({
      variables: {
        offset: pagination.pageSize * pagination.pageIndex,
        limit: pagination.pageSize,
        sort: first(sorting),
        filter: filters,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;

        return fetchMoreResult;
      },
    });
  }, [pagination.pageIndex, pagination.pageSize, sorting, filters, fetchMore]);

  const resetPagination = useCallback(() => {
    setPagination({
      ...paginatedDefaults,
      pageSize: pagination.pageSize,
    });
  }, [pagination.pageSize]);

  const columns = useMemo(() => {
    const availableRequiredFields = clientRequiredFields.map(
      (field) => field.value,
    );

    const requiredFieldsMap = clientRequiredFields.reduce((acc, item) => {
      acc[item.value] = item.label;
      return acc;
    }, {} as EnumRecordType);

    const columnProps: ColumnGenerationProps = {
      columnHelper,
      noShowStatusTypes,
      rosterRoomAssignmentStatuses,
      genders,
      podRequiredFields: requiredFieldsMap,
      availableRequiredFields,
      clientPod,
      isAdmin,
      isOnlyTraveler,
      clientTeamMemberId,
    };

    if (clientPod === ClientPod.Energy) {
      return getEnergyViewColumns(columnProps);
    }
    if (clientPod === ClientPod.GovernmentSolutions) {
      return getGovernmentViewColumns(columnProps);
    }

    return getEnergyViewColumns(columnProps);
  }, [
    clientPod,
    noShowStatusTypes,
    rosterRoomAssignmentStatuses,
    genders,
    clientRequiredFields,
    isAdmin,
    isOnlyTraveler,
    clientTeamMemberId,
  ]);

  // Combine selected rows with the current data, showing selected rows at the top
  const tableData = useMemo(() => {
    const currentData = data?.roster?.items ?? [];

    // Always show selected rows at the top
    if (selectedRowsData.length > 0) {
      const selectedIds = new Set(selectedRowsData.map((row) => row.id));
      const unselectedData = currentData.filter(
        (item) => !selectedIds.has(item.id),
      );
      return [...selectedRowsData, ...unselectedData];
    }

    return currentData;
  }, [data?.roster?.items, selectedRowsData]);

  // Synchronize row selection state when table data changes
  useEffect(() => {
    if (selectedRowsData.length > 0) {
      const newRowSelection: RowSelectionState = {};

      // Create new selection state based on current data indices
      tableData.forEach((row, index) => {
        if (selectedRowsData.some((selected) => selected.id === row.id)) {
          newRowSelection[index] = true;
        }
      });

      setRowSelection(newRowSelection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally not including selectedRowsData to avoid circular updates
  }, [tableData]);

  const tableInstance = useReactTable({
    data: tableData,
    columns: columns.filter(
      (column): column is NonNullable<typeof column> => column !== null,
    ),
    getCoreRowModel: getCoreRowModel(),
    // pagination
    manualPagination: true,
    onPaginationChange: setPagination,
    rowCount: data?.roster?.total,
    // row selection
    enableRowSelection: isOnlyTraveler
      ? (row) => isOwnRosterEntry(row.original.guest?.id)
      : true,
    onRowSelectionChange: (updater) => {
      setRowSelection(updater);
      // Update selected rows data when selection changes
      if (typeof updater === 'function') {
        const newSelection = updater(rowSelection);
        if (!isEmpty(newSelection)) {
          // Get the actual row data for selected rows
          const selectedRows = Object.keys(newSelection)
            .filter((key) => newSelection[key])
            .map((key) => tableData[parseInt(key)]);
          setSelectedRowsData(selectedRows);
        } else {
          setSelectedRowsData([]);
        }
      }
    },
    // sorting
    manualSorting: true,
    onSortingChange: (sortingUpdater) => {
      resetPagination();
      // eslint-disable-next-line
      // @ts-ignore
      const newSortVal = sortingUpdater(sorting);

      // we check here if the internal table sorting state is empty
      // if so we fallback to our default sort values
      // setting defaults here prevents useQueryUrlSorting from causing an extra query without sort values
      if (isEmpty(newSortVal)) {
        setSorting(sortingDefaultValues);
      } else {
        setSorting(sortingUpdater as SortingState);
      }
    },
    enableMultiSort: false,
    // filtering
    manualFiltering: true,
    // table state
    state: {
      sorting,
      pagination,
      rowSelection,
      columnPinning: {
        left: [
          'select',
          'guest',
          ...(clientPod === ClientPod.GovernmentSolutions
            ? ['requiredField_DOD_ID']
            : []),
          'roommates',
          'guest_semiUniqueIdentifier',
        ],
      },
    },
    meta: {
      getCellClass(
        cell: Cell<TableRosterRoomAssignment, unknown>,
      ): string | undefined {
        const isStatusColumn = cell.column.id === 'status';
        const { status } = cell.row.original;

        if (status === RosterRoomAssignmentStatus.Upcoming) {
          return isStatusColumn ? 'bg-blue-100' : 'bg-blue-50';
        }
        if (status === RosterRoomAssignmentStatus.Past) {
          return isStatusColumn ? 'bg-yellow-100' : 'bg-yellow-50';
        }
        if (status === RosterRoomAssignmentStatus.InHouse) {
          return isStatusColumn ? 'bg-green-100' : 'bg-green-50';
        }
      },
    },
  });

  const selectedRows = useMemo(() => {
    if (!isEmpty(rowSelection)) {
      return tableInstance
        .getSelectedRowModel()
        .flatRows.map((row) => row.original.id);
    }

    return [];
  }, [tableInstance, rowSelection]);

  const onSubmit = async (values: RosterFilterInput) => {
    resetPagination();
    const { status: statusArray, city: cityArray } = values;

    // ignore empty arrays
    const status =
      statusArray && statusArray.length > 0 ? statusArray : undefined;

    // ignore empty arrays
    const city = cityArray && cityArray.length > 0 ? cityArray : undefined;

    const valuesToSet = {
      ...values,
      status,
      city,
    };

    setFilters(isEmpty(valuesToSet) ? undefined : valuesToSet);
    setIsSidebarOpen(false);
  };

  const handleRefresh = useCallback(async () => {
    await refetch({
      offset: pagination.pageSize * pagination.pageIndex,
      limit: pagination.pageSize,
      sort: first(sorting),
      filter: filters,
    });

    popToast('Table has been refreshed with the latest changes.');
  }, [filters, pagination.pageIndex, pagination.pageSize, refetch, sorting]);

  const handleFilterUpdate = (values: Partial<RosterFilterInput>) => {
    const newFilters = { ...filters, ...values };
    setFilters(isEmpty(newFilters) ? undefined : newFilters);
  };

  return (
    <div>
      <div className="flex flex-col ">
        <FilterChipsContainer
          openSidebar={() => setIsSidebarOpen(true)}
          filters={filters || {}}
          onUpdateFilter={handleFilterUpdate}
          onSubmit={onSubmit}
        />
        <RosterFilterSidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        >
          <RosterFilterForm
            onSubmit={onSubmit}
            initialFilters={filterDefault}
            onClose={() => setIsSidebarOpen(false)}
          />
        </RosterFilterSidebar>
        <TablePaginationHeader
          handleRefresh={handleRefresh}
          tableInstance={tableInstance}
          action={
            <>
              <TealButton
                className="hidden sm:block"
                onClick={() =>
                  rosterReport({
                    variables: {
                      sort: first(sorting),
                      filter: filters,
                    },
                  })
                }
                disabled={loadingReport}
              >
                Export Table
              </TealButton>
              {(!isOnlyTraveler || hasLinkedProfile) && (
                <div className="hidden sm:block">
                  <PrimaryButtonLink
                    text="New Reservation"
                    to="/requests/new"
                    disabled={isClientInactive}
                  />
                </div>
              )}

              <Button
                className="hidden sm:block"
                text="Refresh"
                onClick={handleRefresh}
              />
              <div className="hidden sm:block">
                <NewRosterRequestsMenu
                  selectedRows={selectedRows}
                  disabled={selectedRows.length === 0 || isClientInactive}
                >
                  <Button
                    className={classNames({
                      'pointer-events-none cursor-default opacity-50':
                        selectedRows.length === 0 || isClientInactive,
                    })}
                  >
                    Create Change Request
                    {selectedRows.length > 0 && (
                      <> for Selected ({selectedRows.length})</>
                    )}
                  </Button>
                </NewRosterRequestsMenu>
              </div>
            </>
          }
        />
        {isOnlyTraveler && !hasLinkedProfile && (
          <div className="mb-4 flex items-start gap-3 rounded-lg bg-yellow-50 p-4">
            <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0 text-yellow-600" />
            <div>
              <p className="font-medium text-yellow-800">Account Not Linked</p>
              <p className="mt-1 text-sm text-yellow-700">
                Your account is not linked to a team member profile. Please
                contact your administrator to book reservations.
              </p>
            </div>
          </div>
        )}
        <Table tableInstance={tableInstance} loading={loadingRoster} />
        <TablePaginationFooter tableInstance={tableInstance} />

        {/* Mobile Action Buttons */}
        <div className="fixed inset-x-0 bottom-0 z-40 flex flex-col border-t border-gray-200 bg-gray-200 shadow-lg sm:hidden">
          {/* Selected Guests Section */}
          {Object.keys(rowSelection).length > 0 && (
            <div className=" m-4 flex items-center justify-between rounded-xl bg-white px-4 py-3">
              <div className="font-medium text-gray-900">
                {Object.keys(rowSelection).length} guests selected
              </div>
              <Button
                onClick={() => {
                  setRowSelection({});
                  setSelectedRowsData([]);
                }}
                className="text-sm font-medium text-black"
              >
                Clear Selected
              </Button>
            </div>
          )}
          {/* Action Buttons */}
          <div className="flex gap-3 p-4">
            {(!isOnlyTraveler || hasLinkedProfile) && (
              <div className="w-full">
                <PrimaryButtonLink
                  text="New Reservation"
                  to="/requests/new"
                  disabled={isClientInactive}
                  className="w-full justify-center"
                />
              </div>
            )}
            <NewRosterRequestsMenu
              selectedRows={selectedRows}
              disabled={selectedRows.length === 0 || isClientInactive}
            >
              <SecondaryButton
                className={classNames('w-full justify-center', {
                  'pointer-events-none cursor-default opacity-50':
                    selectedRows.length === 0 || isClientInactive,
                })}
                disabled={selectedRows.length === 0 || isClientInactive}
              >
                Request Change
              </SecondaryButton>
            </NewRosterRequestsMenu>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RosterTable;
